import React from "react";
import AriaTooltip from "../../../../../../../Shared/Tooltip/AriaTooltip";
import { FrequencyTypeMap } from "../../../../../../Resoures/Contents";
import { Urls } from "../../../../../../Urls";
import { Navigator } from "../../../../../../../Shared/Wizards/Entities/Navigator";
import { checkPermission } from "../../../../../../../Shared/Permission/PermissionHelper";
import { UserClaim } from "../../../../../../../../Wrap/Constants/Enums/UserClaim";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { CommonHelper } from "../../../../../../../../react-core/Helpers/CommonHelper";
import { StaticUrls } from "../../../../../../Resoures/StaticUrls";
import { MoneyDisplayHelper } from "../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { calculateGrossIncomePerPayment, calculateGrossIncomePerPaymentForTargetIncomeAlgorithm, calculateNextIncomePerPayment } from "../../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";
import { PensionLevelDrawdownIncome } from "../../../../../../../../Wrap/Models/Domains/Drawdown/PensionLevelDrawdownIncome";
import { ClientAccount } from "../../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { PartLoading } from "../../../../../../../../react-core/Html/PartLoading";
import { PensionSubAccountSettings } from "../../../../../../../../Wrap/Models/Domains/SubAccount/PensionSubAccountSettings";
import { InProgressDrawdownIncome } from "../../../../../../../../Wrap/Models/Domains/Drawdown/InProgressDrawdownIncome";
import { IJsonDate } from "../../../../../../../../Wrap/System/IJsonDate";

interface INextIncomePaymentProps {
    data: PensionLevelDrawdownIncome;
    InProgressDrawdownIncome: InProgressDrawdownIncome[];
    taxInfo: any;
    headaccount: ClientAccount;
    subAccountHierarchyId: string;
    customerid: number;
    isPensionSubAccountSettingsLoading?: boolean;
    pensionSubAccountSettings?: PensionSubAccountSettings;
    setShowRestrictionLightbox?: (val: boolean) => void;
    setRestrictionLightboxErrorMessage?: (val: string) => void;
}
export const NextIncomePayment = (props: INextIncomePaymentProps) => {
    if (props.isPensionSubAccountSettingsLoading) {
        return (
            <PartLoading isLoading={true}>
                <div className="table-skeleton"></div>
            </PartLoading>
        )
    }

    const onEditButtonClick = () => {
        if (props.pensionSubAccountSettings?.HasDisqualifyingPensionCredit === true) {
            props.setShowRestrictionLightbox(true);
            props.setRestrictionLightboxErrorMessage("This plan includes disqualifying pension credit. Unfortunately, instructions cannot be completed online. Please contact us on 0345 279 1001.");
            return;
        }

        window.location.assign(
            StaticUrls.EditIncomeWithdrawalsUrl(
                null,
                props.headaccount.HeadClAccountId,
                props.headaccount.HierarchyId,
                props.subAccountHierarchyId,
                false
            ))
    }

    const onCancelButtonClick = () => {
        if (props.pensionSubAccountSettings?.HasDisqualifyingPensionCredit === true) {
            props.setShowRestrictionLightbox(true);
            props.setRestrictionLightboxErrorMessage("This plan includes disqualifying pension credit. Unfortunately, instructions cannot be completed online. Please contact us on 0345 279 1001.");
            return;
        }

        Navigator.navigateTo(Urls.get().EditIncomeWithdrawalsIncomePaymenDetails, {
            scrollAfterNavigating: true,
            queryParamsObject: {
                customerId: props.customerid,
                clAccountId: props.headaccount.HeadClAccountId,
                hierarchyid: props.headaccount.HierarchyId,
                subAccountHierarchyId: props.subAccountHierarchyId
            },
        });
    }

    const getFirstPayDateForTargetIncomeAlgorithm = (income: PensionLevelDrawdownIncome): IJsonDate => {
        let firstDate: IJsonDate = null;

        (income?.FlexiArrangementsIncomeDetail ?? []).forEach((item) => {
            if (firstDate == null) { firstDate = item.NextPayment }
            if (item.NextPayment < firstDate) { firstDate = item.NextPayment; }
        });

        (income?.CappedArrangementsIncomeDetail ?? []).forEach((item) => {
            if (firstDate == null) { firstDate = item.NextPayment }
            if (item.NextPayment < firstDate) { firstDate = item.NextPayment; }
        });
        
        return firstDate;
    }

    let nextPaymentDate = props.data?.AnnualIncome?.TargetSpecificArrangements ? getFirstPayDateForTargetIncomeAlgorithm(props.data) : props.data.AnnualIncome?.NextPayment;
    let nextIncomePayment = props.data?.AnnualIncome?.TargetSpecificArrangements ? calculateGrossIncomePerPaymentForTargetIncomeAlgorithm(props.data, getFirstPayDateForTargetIncomeAlgorithm(props.data)) : calculateGrossIncomePerPayment(props.data.AnnualIncome);

    if (props.InProgressDrawdownIncome.length > 0) {
        nextPaymentDate = props.InProgressDrawdownIncome.reduce((min, item) =>
            min.PaymentDate < item.PaymentDate ? min : item).PaymentDate;        

        nextIncomePayment = calculateNextIncomePerPayment(props.InProgressDrawdownIncome.filter(x => x.PaymentDate = nextPaymentDate));
    }

    return (
        <>
            <div className="row">
                <div className="col-fluid">
                    <h3>Next income payment</h3>
                </div>
                <div className="col-auto">
                    {/* add event handler here for onclick event to navigate to edit income payment wizard when available */}
                    {checkPermission(UserClaim.AdviserPortalEditIncomePaymentsReadWrite) && (
                        <button
                            type="button"
                            className="edit-circle-icon-button"
                            title="Edit"
                            onClick={onEditButtonClick}>
                            <span className="icon"></span>
                            <span className="sr-only">Edit next income payment</span>
                        </button>
                    )}
                </div>
            </div>
            <div className="panel-subtle pt-10 pb-12 f-grow-1 mb-0">
                <table className="table-subtle table-first-last-cell-padding-reset has-first-cell-highlighted">
                    <tbody>
                        <tr>
                            <th scope="row">Amount</th>
                            <td className="cell-right">{MoneyDisplayHelper.getMoneyFormat(nextIncomePayment)}</td>
                        </tr>
                        <tr>
                            <th scope="row">Frequency</th>
                            <td className="cell-right">{FrequencyTypeMap[props.data.AnnualIncome?.Frequency]}</td>
                        </tr>
                        <tr>
                            <th scope="row">Next payment date</th>
                            <td className="cell-right">{JsonDateParser.getFormatDate(
                                JsonDateParser.toDate(nextPaymentDate),
                                "dd/MM/yyyy"
                            )}</td>
                        </tr>
                        <tr>
                            <th scope="row">
                                PAYE tax code{" "}
                                <AriaTooltip cssWrapper="is-info-icon" showCloseButton={true}>
                                    We do not know your client's overall tax situation outwith Aberdeen, therefore this will be the
                                    tax code used for any payments out. The tax they pay upfront may be different to their final tax
                                    position. HMRC could come back at any time with a revised code.
                                </AriaTooltip>
                            </th>
                            <td className="cell-right">
                                {CommonHelper.isNullOrEmpty(props.taxInfo?.PayeTaxCode)
                                    ? "N/A"
                                    : props.taxInfo?.PayeTaxCode}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div>
                    {checkPermission(UserClaim.AdviserPortalCancelRegularCrystalisationReadWrite) && (
                        <button
                            className="hyperlink"
                            onClick={onCancelButtonClick}
                        >
                            Cancel income payment
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};
